import React from 'react';
import { 
  FiMonitor, 
  FiShoppingCart, 
  FiCheckCircle,
  FiArrowRight,
  FiCode,
  FiZap,
  FiLayout
} from 'react-icons/fi';
import { Image } from '../../components/ui/Image';
import { imageConfig } from '../../config/images';
import { Link } from 'react-router-dom';

export const WebDesignPage: React.FC = () => {
  const designServices = [
    {
      title: 'Responsive Website Design',
      description: 'Mobile-first websites that look perfect on all devices',
      icon: <FiMonitor className="text-4xl" />,
      features: [
        'Custom Design Mockups',
        'Mobile-First Approach',
        'Cross-Browser Compatible',
        'Fast Loading Speed',
        'SEO-Friendly Structure',
        'Modern UI/UX Design'
      ],
      price: '₹50,000',
      duration: 'starting price',
      timeline: '4-6 weeks'
    },
    {
      title: 'E-commerce Website',
      description: 'Complete online store with payment integration',
      icon: <FiShoppingCart className="text-4xl" />,
      features: [
        'Product Catalog Setup',
        'Shopping Cart System',
        'Payment Gateway Integration',
        'Inventory Management',
        'Order Tracking',
        'Customer Accounts'
      ],
      price: '₹1,50,000',
      duration: 'starting price',
      timeline: '8-12 weeks'
    },
    {
      title: 'Landing Page Design',
      description: 'High-converting landing pages for campaigns',
      icon: <FiLayout className="text-4xl" />,
      features: [
        'Conversion-Focused Design',
        'A/B Testing Ready',
        'Lead Capture Forms',
        'Fast Loading',
        'Analytics Integration',
        'CTA Optimization'
      ],
      price: '₹20,000',
      duration: '/page',
      timeline: '1-2 weeks'
    },
    {
      title: 'WordPress Development',
      description: 'Custom WordPress sites with easy content management',
      icon: <FiCode className="text-4xl" />,
      features: [
        'Custom Theme Development',
        'Plugin Integration',
        'WooCommerce Setup',
        'SEO Optimization',
        'Security Hardening',
        'Training & Support'
      ],
      price: '₹75,000',
      duration: 'starting price',
      timeline: '6-8 weeks'
    },
    {
      title: 'Web Application Development',
      description: 'Custom web apps with advanced functionality',
      icon: <FiZap className="text-4xl" />,
      features: [
        'Custom Functionality',
        'Database Design',
        'API Integration',
        'User Authentication',
        'Admin Dashboard',
        'Cloud Hosting Setup'
      ],
      price: '₹2,00,000',
      duration: 'starting price',
      timeline: '12-16 weeks'
    },
    {
      title: 'Website Redesign',
      description: 'Modernize your existing website for better results',
      icon: <FiMonitor className="text-4xl" />,
      features: [
        'Design Audit',
        'Modern UI Refresh',
        'Performance Optimization',
        'SEO Improvements',
        'Content Migration',
        'Mobile Optimization'
      ],
      price: '₹60,000',
      duration: 'starting price',
      timeline: '4-6 weeks'
    }
  ];

  const designPackages = [
    {
      name: 'Basic Website',
      price: '₹50,000',
      period: 'one-time',
      description: 'Perfect for small businesses and startups',
      features: [
        'Up to 5 pages',
        'Responsive design',
        'Contact form',
        'Basic SEO setup',
        'Google Analytics',
        '3 months support'
      ],
      popular: false
    },
    {
      name: 'Professional Website',
      price: '₹1,25,000',
      period: 'one-time',
      description: 'Advanced features for growing businesses',
      features: [
        'Up to 15 pages',
        'Custom design',
        'CMS integration',
        'Advanced SEO',
        'Blog setup',
        'Lead capture forms',
        '6 months support'
      ],
      popular: true
    },
    {
      name: 'Enterprise Website',
      price: 'Custom',
      period: 'pricing',
      description: 'Complex websites with custom features',
      features: [
        'Unlimited pages',
        'Custom functionality',
        'Advanced integrations',
        'Multi-language support',
        'User portals',
        'Dedicated project manager',
        '12 months support'
      ],
      popular: false
    }
  ];

  const results = [
    {
      metric: '250+',
      label: 'Websites Built'
    },
    {
      metric: '+180%',
      label: 'Average Conversion Increase'
    },
    {
      metric: '< 3 sec',
      label: 'Average Load Time'
    },
    {
      metric: '100%',
      label: 'Mobile Responsive'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-32 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-6xl font-bold mb-8">
            Web Design & <span className="text-yellow-400">Development</span>
          </h1>
          <p className="text-2xl mb-12 max-w-4xl mx-auto opacity-90">
            Create stunning, conversion-optimized websites that captivate visitors 
            and drive business growth.
          </p>
          
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {results.map((result, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl font-bold text-yellow-400 mb-2">{result.metric}</div>
                <div className="text-lg">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Beautiful Designs That Drive Results</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We create stunning, user-friendly websites that not only look great but also 
              convert visitors into customers.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative group">
                <Image 
                  src={imageConfig.portfolio.project1} 
                  alt="Website Design Example 1" 
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-white text-center p-6">
                    <h3 className="text-xl font-bold mb-2">E-commerce Website</h3>
                    <p className="text-sm">Modern, fast, and optimized for conversions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative group">
                <Image 
                  src={imageConfig.portfolio.project2} 
                  alt="Website Design Example 2" 
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-white text-center p-6">
                    <h3 className="text-xl font-bold mb-2">Corporate Website</h3>
                    <p className="text-sm">Professional design with seamless UX</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300">
              <div className="relative group">
                <Image 
                  src={imageConfig.portfolio.project3} 
                  alt="Website Design Example 3" 
                  className="w-full h-64 object-cover"
                />
                <div className="absolute inset-0 bg-blue-600 bg-opacity-90 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="text-white text-center p-6">
                    <h3 className="text-xl font-bold mb-2">Landing Page</h3>
                    <p className="text-sm">High-converting design with clear CTAs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Web Design Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional website design and development services for every business need
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {designServices.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-all duration-300 group">
                <div className="text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3 text-gray-700">
                      <FiCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-6">
                  <div className="mb-4">
                    <div className="text-2xl font-bold text-blue-600">{service.price}</div>
                    <div className="text-sm text-gray-600">{service.duration}</div>
                    <div className="text-sm text-gray-500 mt-1">Timeline: {service.timeline}</div>
                  </div>
                  <Link
                    to="/contact"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 group"
                  >
                    Get Started
                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Website Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the perfect website package for your business
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {designPackages.map((pkg, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl shadow-lg p-8 relative ${
                  pkg.popular ? 'border-2 border-blue-500 transform scale-105' : 'border border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{pkg.name}</h3>
                  <div className="text-4xl font-bold text-blue-600 mb-2">
                    {pkg.price}
                  </div>
                  <div className="text-sm text-gray-600 mb-2">{pkg.period}</div>
                  <p className="text-gray-600">{pkg.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <FiCheckCircle className="text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Our Web Design Services?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We build websites that look amazing and convert visitors into customers
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiMonitor className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Modern Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Beautiful, contemporary designs that reflect your brand and engage visitors
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiZap className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Lightning Fast</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized for speed with average load times under 3 seconds
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500 to-indigo-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCheckCircle className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Conversion-Focused</h3>
              <p className="text-gray-600 leading-relaxed">
                Strategic design elements that guide visitors towards taking action
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Web Design FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Web Design FAQs</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about our website design and development services
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "How long does it take to build a website?",
                answer: "Timeline varies by project complexity. A basic website takes 4-6 weeks, professional sites 8-12 weeks, and complex e-commerce or custom applications 12-16 weeks. We provide detailed timelines during consultation."
              },
              {
                question: "Will my website be mobile-friendly?",
                answer: "Absolutely! All our websites are built with a mobile-first approach, ensuring perfect functionality across all devices - smartphones, tablets, and desktops. We test extensively on various screen sizes."
              },
              {
                question: "Can I update the website myself?",
                answer: "Yes! We build websites with user-friendly content management systems (CMS) like WordPress or custom admin panels. We also provide training and documentation so you can easily update content, images, and more."
              },
              {
                question: "Do you provide website hosting?",
                answer: "We can recommend reliable hosting providers or help you set up hosting. We also offer managed hosting services where we handle all technical aspects including security, backups, and updates."
              },
              {
                question: "Will my website be SEO-friendly?",
                answer: "Yes! We build SEO foundations into every website including proper site structure, fast loading speeds, mobile optimization, clean code, and basic on-page SEO. We can also provide ongoing SEO services."
              },
              {
                question: "What if I need changes after launch?",
                answer: "We offer support packages for post-launch updates and maintenance. Minor tweaks are included in our warranty period. For major changes, we provide affordable hourly rates or maintenance packages."
              },
              {
                question: "Can you redesign my existing website?",
                answer: "Definitely! We specialize in website redesigns. We'll audit your current site, identify improvement opportunities, and create a modern, high-performing website while preserving your SEO rankings and content."
              },
              {
                question: "What platforms do you work with?",
                answer: "We work with various platforms including WordPress, Shopify, custom React/Node.js applications, and more. We recommend the best platform based on your specific needs, budget, and technical requirements."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-blue-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-blue-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Build Your Dream Website?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Let's create a stunning website that showcases your brand and 
            converts visitors into customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-yellow-400 text-gray-800 px-10 py-5 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl"
            >
              Get Free Quote
            </Link>
            <Link
              to="/portfolio"
              className="border-2 border-white text-white px-10 py-5 rounded-lg font-bold text-lg hover:bg-white hover:text-blue-600 transition-all duration-300"
            >
              View Portfolio
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};